import PdfPreview from "../ui/PdfPreview"
import { Dispatch, SetStateAction } from "react"
import { SpinnerText } from "../ui/Spinner"
import FileUploadBox from "../ui/FileUploadBox"
import InfoBox from "../ui/InfoBox"
import QuestionBox from "../ui/QuestionBox"
import { countries } from "../../utils/countryData"
import {
  DesignDetails,
  DesignSearchResultItem
} from "../../services/designSearchService"

type PriorityEntryMode = "wipo" | "manual" | "database" | null

type DesignClaimsSectionProps = {
  priorityClaims: any[]
  claimsOpen: boolean
  setClaimsOpen: (value: boolean) => void
  priorityOptionsOpen: boolean
  setPriorityOptionsOpen: (value: boolean) => void
  priorityEntryMode: PriorityEntryMode
  setPriorityEntryMode: (value: PriorityEntryMode) => void
  resetPriorityEntry: () => void
  selectPriorityEntryMode: (mode: "wipo" | "manual" | "database") => void
  transferDocumentChecked: boolean
  setTransferDocumentChecked: (value: boolean) => void
  transferDocumentFile: File | null
  setTransferDocumentFile: (value: File | null) => void
  transferDocumentError: string
  setTransferDocumentError: (value: string) => void
  handleTransferDocumentSelect: (file: File) => void
  manualPriorityCountry: string
  setManualPriorityCountry: (value: string) => void
  manualPriorityDate: string
  setManualPriorityDate: (value: string) => void
  manualPriorityNumber: string
  setManualPriorityNumber: (value: string) => void
  manualPriorityFile: File | null
  setManualPriorityFile: (value: File | null) => void
  manualPriorityFileError: string
  setManualPriorityFileError: (value: string) => void
  handlePriorityDocumentSelect: (file: File) => void
  isManualPriorityValid: string | File | boolean | null
  databasePriorityCountry: string
  databasePriorityId: string
  setDatabasePriorityId: (value: string) => void
  databasePriorityResults: DesignSearchResultItem[]
  setDatabasePriorityResults: Dispatch<SetStateAction<DesignSearchResultItem[]>>
  databasePrioritySelected: DesignDetails | null
  setDatabasePrioritySelected: (value: DesignDetails | null) => void
  databasePriorityOpen: boolean
  setDatabasePriorityOpen: (value: boolean) => void
  databasePriorityLoading: boolean
  databasePriorityError: string
  handleImportDatabasePriority: () => void
  addingPriority: boolean
  addPriorityError: string
  handleAddManualPriority: () => void
  handleAddDatabasePriority: () => void
  handleRemovePriorityClaim: (index: number) => void
}

export default function DesignClaimsSection({
  priorityClaims,
  claimsOpen,
  setClaimsOpen,
  priorityOptionsOpen,
  setPriorityOptionsOpen,
  priorityEntryMode,
  setPriorityEntryMode,
  resetPriorityEntry,
  selectPriorityEntryMode,
  transferDocumentChecked,
  setTransferDocumentChecked,
  transferDocumentFile,
  setTransferDocumentFile,
  transferDocumentError,
  setTransferDocumentError,
  handleTransferDocumentSelect,
  manualPriorityCountry,
  setManualPriorityCountry,
  manualPriorityDate,
  setManualPriorityDate,
  manualPriorityNumber,
  setManualPriorityNumber,
  manualPriorityFile,
  setManualPriorityFile,
  manualPriorityFileError,
  setManualPriorityFileError,
  handlePriorityDocumentSelect,
  isManualPriorityValid,
  databasePriorityCountry,
  databasePriorityId,
  setDatabasePriorityId,
  databasePriorityResults,
  setDatabasePriorityResults,
  databasePrioritySelected,
  setDatabasePrioritySelected,
  databasePriorityOpen,
  setDatabasePriorityOpen,
  databasePriorityLoading,
  databasePriorityError,
  handleImportDatabasePriority,
  addingPriority,
  addPriorityError,
  handleAddManualPriority,
  handleAddDatabasePriority,
  handleRemovePriorityClaim
}: DesignClaimsSectionProps) {
  return (
<div className="border-b-2 border-dashed border-gray-300 py-4">
  <div className="flex items-center gap-3">
    <button
      type="button"
      onClick={() => {
        setClaimsOpen(!claimsOpen)
        if (claimsOpen) {
          setPriorityOptionsOpen(false)
          setPriorityEntryMode(null)
        }
      }}
      className={`relative h-8 w-16 rounded-full transition-colors ${
        claimsOpen ? "bg-blue-800" : "bg-gray-400"
      }`}
    >
      <span
        className={`absolute left-1 top-1 h-6 w-6 rounded-full bg-white transition-transform ${
          claimsOpen ? "translate-x-8" : "translate-x-0"
        }`}
      />
    </button>

    <h4 className="text-lg font-semibold text-blue-800">
      Claims
    </h4>

    <QuestionBox title="Claims">
      You can add priority claims for this design.
    </QuestionBox>
  </div>

  {claimsOpen && (
    <div className="mt-5 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <h5 className="text-base font-semibold text-gray-800">
          Priority claim
        </h5>

        <InfoBox title="Priority claim">
          You can claim priority from an earlier filing for this
          design.
        </InfoBox>
      </div>

      <div
        onClick={() => {
          setPriorityOptionsOpen(true)
          setPriorityEntryMode(null)
        }}
        className="flex w-60 cursor-pointer flex-col items-center rounded border bg-gray-50 p-8 transition hover:border-orange-500"
      >
        <div className="mb-2 text-4xl text-gray-700">
          +
        </div>

        <span className="text-sm font-medium text-gray-700">
          Add priority claim
        </span>
      </div>

      {priorityClaims.length > 0 && (
        <div className="rounded border border-blue-100 bg-blue-50 p-3 text-sm">
          <p className="mb-2 font-semibold text-blue-900">
            Added priority claims
          </p>
          <div className="space-y-2">
            {priorityClaims.map((priority, index) => (
              <div
                key={`${priority.applicationNumber || priority.st13ApplicationNumber || index}`}
                className="flex items-center justify-between gap-3 rounded bg-white px-3 py-2"
              >
                <span>
                  {priority.country || "EUIPO"} ·{" "}
                  {priority.applicationNumber ||
                    priority.st13ApplicationNumber ||
                    "-"}{" "}
                  {priority.applicationDate
                    ? `· ${new Date(priority.applicationDate).toLocaleDateString("en-GB")}`
                    : ""}
                </span>
                <PdfPreview documentId={priority.copyFirstFilingDocument} />
                {priority.transferAssignmentDocument && <span className="text-xs">Transfer document: <PdfPreview documentId={priority.transferAssignmentDocument} /></span>}
                <button
                  type="button"
                  onClick={() => handleRemovePriorityClaim(index)}
                  className="text-xs font-semibold text-gray-600 hover:text-blue-800"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {addPriorityError && (
        <p className="text-sm font-semibold text-red-600">
          {addPriorityError}
        </p>
      )}

      {priorityOptionsOpen && (
        <div className="relative rounded border bg-white p-6 text-sm text-gray-700">
          <button
            type="button"
            onClick={resetPriorityEntry}
            className="absolute right-2 top-2 text-lg text-gray-400 hover:text-gray-600"
          >
            ×
          </button>

          <p className="mb-4 font-semibold">
            How would you like to add your priority claim?
          </p>

          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <button
              type="button"
              onClick={() => selectPriorityEntryMode("wipo")}
              className={`rounded border border-blue-700 px-5 py-2 text-sm font-medium ${
                priorityEntryMode === "wipo" || priorityEntryMode === null
                  ? "bg-blue-700 text-white"
                  : "text-blue-700 hover:bg-blue-50"
              }`}
            >
              Use WIPO-DAS code
            </button>

            <span className="hidden text-sm font-semibold text-gray-600 sm:inline">
              - OR -
            </span>

            <button
              type="button"
              onClick={() => selectPriorityEntryMode("manual")}
              className={`rounded border border-blue-700 px-5 py-2 text-sm font-medium ${
                priorityEntryMode === "manual"
                  ? "bg-blue-700 text-white"
                  : "text-blue-700 hover:bg-blue-50"
              }`}
            >
              Enter manually
            </button>

            <button
              type="button"
              onClick={() => selectPriorityEntryMode("database")}
              className={`rounded border border-blue-700 px-5 py-2 text-sm font-medium ${
                priorityEntryMode === "database"
                  ? "bg-blue-700 text-white"
                  : "text-blue-700 hover:bg-blue-50"
              }`}
            >
              Import from database
            </button>
          </div>

          {priorityEntryMode === "wipo" && (
            <div className="mt-6 space-y-4 border-t border-gray-200 pt-5">
              <div>
                <label className="flex items-center text-xs font-semibold text-gray-500">
                  WIPO-DAS code *
                  <QuestionBox title="WIPO-DAS code">
                    With the WIPO Digital Access Service (DAS), you
                    can meet the requirements for submitting
                    priority and similar documents at participating
                    offices without having to prepare and submit
                    multiple certified paper copies. Instead,
                    please enter the office where priority is
                    claimed as the second filing office, which
                    accesses a copy of the document directly from a
                    digital library normally maintained by the
                    office or organisation with which the earlier
                    application was filed.
                  </QuestionBox>
                </label>
                <input
                  type="text"
                  className="mt-2 w-full rounded border-2 border-blue-800 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-800"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500">
                  Country of the first filing *
                </label>
                <select className="mt-2 w-full rounded border-2 border-blue-800 px-3 py-2 text-sm text-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-800">
                  <option value="">- Select -</option>
                </select>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold text-gray-500">
                    Filing date *
                  </label>
                  <input
                    type="text"
                    placeholder="dd/mm/yyyy"
                    className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-800"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500">
                    Filing number *
                  </label>
                  <input
                    type="text"
                    className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-800"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  checked={transferDocumentChecked}
                  onChange={(event) => {
                    setTransferDocumentChecked(event.target.checked)
                    if (!event.target.checked) {
                      setTransferDocumentFile(null)
                      setTransferDocumentError("")
                    }
                  }}
                  className="h-4 w-4 accent-blue-800"
                />
                Transfer document / transfer
                <QuestionBox title="Transfer document / transfer">
                  Please tick this box to attach the document
                  proving the transfer of the design if you want
                  this information to be taken into account in the
                  file. Please note that the content of the
                  attachment will not be checked or examined by the
                  examiner. The attachment is kept for archiving
                  purposes only.
                </QuestionBox>
              </label>

              {transferDocumentChecked && (
                <FileUploadBox
                  label="Drag your document here or "
                  clickLabel="click to select"
                  helperText="PDF or JPEG, max. 2 MB"
                  uploadedFile={transferDocumentFile}
                  error={transferDocumentError}
                  onFileSelect={handleTransferDocumentSelect}
                  onDelete={() => {
                    setTransferDocumentFile(null)
                    setTransferDocumentError("")
                  }}
                  accept="application/pdf,image/jpeg"
                  preview={false}
                  className="border-blue-700 bg-white py-10 hover:border-blue-700 hover:bg-blue-50"
                  linkClassName="text-blue-700 underline hover:text-blue-800"
                />
              )}

              <div className="flex justify-end gap-4 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={resetPriorityEntry}
                  className="px-4 py-2 text-sm font-semibold uppercase text-blue-800 hover:text-blue-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled
                  className="rounded border border-gray-400 bg-gray-300 px-6 py-2 text-sm font-semibold uppercase text-white cursor-not-allowed"
                >
                  + Add priority
                </button>
              </div>
            </div>
          )}

          {priorityEntryMode === "manual" && (
            <div className="mt-4 border-t pt-4 space-y-6 text-sm">
              <div>
                <label className="block mb-1 font-medium">
                  Country of the first filing *
                </label>
                <select
                  value={manualPriorityCountry}
                  onChange={(event) =>
                    setManualPriorityCountry(event.target.value)
                  }
                  className="w-full border rounded p-1.5"
                >
                  <option value="">– Select –</option>
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block mb-1 font-medium">
                    Filing date *
                  </label>
                  <input
                    type="date"
                    value={manualPriorityDate}
                    onChange={(event) =>
                      setManualPriorityDate(event.target.value)
                    }
                    className="w-full border rounded p-1.5"
                  />
                </div>

                <div>
                  <label className="block mb-1 font-medium">
                    Filing number *
                  </label>
                  <input
                    type="text"
                    value={manualPriorityNumber}
                    onChange={(event) =>
                      setManualPriorityNumber(event.target.value)
                    }
                    className="w-full border rounded p-1.5"
                  />
                </div>
              </div>

              <div className="border-t pt-3">
                <p className="font-medium mb-2">
                  Copy of the first filing *
                </p>
                <FileUploadBox
                  uploadedFile={manualPriorityFile}
                  error={manualPriorityFileError}
                  onFileSelect={handlePriorityDocumentSelect}
                  onDelete={() => {
                    setManualPriorityFile(null)
                    setManualPriorityFileError("")
                  }}
                  accept="application/pdf,image/jpeg"
                  preview={false}
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t">
                <button
                  type="button"
                  onClick={resetPriorityEntry}
                  className="text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddManualPriority}
                  disabled={!isManualPriorityValid || addingPriority}
                  className={`px-3 py-1.5 rounded text-sm font-medium ${
                    isManualPriorityValid && !addingPriority
                      ? "bg-blue-700 text-white hover:bg-blue-800"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {addingPriority ? <SpinnerText>Adding...</SpinnerText> : "Add priority"}
                </button>
              </div>
            </div>
          )}

          {priorityEntryMode === "database" && (
            <div className="mt-4 space-y-5 border-t pt-4 text-sm">
              <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
                <div>
                  <label className="mb-1 block font-medium text-gray-500">
                    Country of the first filing
                  </label>
                  <input
                    type="text"
                    value={databasePriorityCountry || "EUIPO"}
                    readOnly
                    className="w-full rounded border border-gray-300 bg-gray-100 px-3 py-2 text-gray-400"
                  />
                </div>

                <div className="relative">
                  <label className="mb-1 block font-medium text-gray-500">
                    Priority number or design ID
                  </label>
                  <input
                    type="text"
                    value={databasePriorityId}
                    onChange={(event) => {
                      setDatabasePriorityId(event.target.value)
                      setDatabasePrioritySelected(null)
                    }}
                    onFocus={() => {
                      if (
                        databasePriorityResults.length > 0 ||
                        databasePriorityLoading ||
                        databasePriorityError
                      ) {
                        setDatabasePriorityOpen(true)
                      }
                    }}
                    className="w-full rounded border-2 border-blue-800 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-800"
                  />

                  {databasePriorityOpen && (
                    <div className="absolute left-0 right-0 top-full z-30 max-h-96 overflow-y-auto border-2 border-blue-800 bg-white text-sm shadow-sm">
                      {databasePriorityLoading && (
                        <div className="px-4 py-2 text-gray-500">
                          <SpinnerText>Loading...</SpinnerText>
                        </div>
                      )}

                      {databasePriorityError && (
                        <div className="px-4 py-2 text-red-600">
                          {databasePriorityError}
                        </div>
                      )}

                      {!databasePriorityLoading &&
                        !databasePriorityError &&
                        databasePriorityResults.length === 0 && (
                        <div className="px-4 py-2 text-gray-500">
                          No options available
                        </div>
                      )}

                      {!databasePriorityLoading &&
                        !databasePriorityError &&
                        databasePriorityResults.map((design) => (
                          <button
                            key={design.designNumber}
                            type="button"
                            onClick={() => {
                              setDatabasePriorityId(design.designNumber)
                              setDatabasePrioritySelected(null)
                              setDatabasePriorityResults([])
                              setDatabasePriorityOpen(false)
                            }}
                            className="grid w-full grid-cols-[1fr_90px] border-b border-gray-200 px-4 py-2 text-left text-gray-800 last:border-b-0 hover:bg-blue-50"
                          >
                            <span>{design.designNumber}</span>
                            <span className="text-right text-blue-700">
                              View
                            </span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleImportDatabasePriority}
                  disabled={!databasePriorityId.trim() || databasePriorityLoading}
                  className={`mt-6 rounded px-5 py-2 text-sm font-semibold uppercase text-white ${
                    databasePriorityId.trim() && !databasePriorityLoading
                      ? "bg-blue-800 hover:bg-blue-700"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  Import
                </button>
              </div>

              {databasePrioritySelected && (
                <div className="grid gap-3 border-t border-gray-200 pt-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block font-medium text-gray-500">
                      Application number
                    </label>
                    <input
                      type="text"
                      value={databasePrioritySelected.applicationNumber}
                      readOnly
                      className="w-full rounded border border-gray-300 bg-gray-100 px-3 py-2 text-gray-400"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block font-medium text-gray-500">
                      Filing date
                    </label>
                    <input
                      type="text"
                      value={new Date(databasePrioritySelected.applicationDate).toLocaleDateString("en-GB")}
                      readOnly
                      className="w-full rounded border border-gray-300 bg-gray-100 px-3 py-2 text-gray-400"
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 border-t pt-3">
                <button
                  type="button"
                  onClick={resetPriorityEntry}
                  className="px-4 py-2 text-sm font-semibold uppercase text-blue-800 hover:text-blue-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddDatabasePriority}
                  disabled={!databasePrioritySelected}
                  className={`rounded px-6 py-2 text-sm font-semibold uppercase text-white ${
                    databasePrioritySelected
                      ? "bg-blue-800 hover:bg-blue-700"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  + Add priority
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )}
</div>

  )
}
